import { ShareOutlined } from "@mui/icons-material";
import { useState } from "react";
import AAHeaderButton from "./header/AAHeaderButton";
import ChatShareModal from "./ChatShareModal";

/**
 * The chat toolbar's Share control: an `AAHeaderButton` over `ChatShareModal`.
 *
 * Every host of this button sits on the chat's own page, which is why the
 * link it shares is the current URL. A surface that lists chats it is not
 * showing — a history row — must build the link from the chat's id and render
 * `ChatShareModal` itself.
 */
const ChatShareButton = ({
  dialogueId,
  shareUrl,
  isMobile = false,
}: {
  dialogueId: number;
  /** Defaults to the current page, which is this chat. */
  shareUrl?: string;
  isMobile?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  // Captured on press, not on render: a chat created in place writes its id
  // into the URL after this button has already mounted, and the link must be
  // the one the reader would get by pressing Share right now. Only when the
  // host has not supplied a link — with one, this is never read.
  const [currentUrl, setCurrentUrl] = useState("");

  return (
    <div>
      <AAHeaderButton
        icon={<ShareOutlined />}
        label="Share"
        onClick={() => {
          if (!shareUrl) setCurrentUrl(window.location.href);
          setOpen(true);
        }}
        tooltip="Share"
        variant="outlined"
        isMobile={isMobile}
        data-testid="aa-share-btn"
        data-attr="assistant:btn:open-share-modal"
      />
      {/* Mounted only while open. The modal reads the dialogue and prepares
          the share mutation in its own body, and `useGetDialogueById` carries
          a ~115-line `select` that JSON-parses every message — under
          react-query v3 an inline `select` re-runs on each render, so a closed
          modal made every host header re-parse the whole conversation. */}
      {open && (
        <ChatShareModal
          dialogueId={dialogueId}
          shareUrl={shareUrl ?? currentUrl}
          open
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
};

export default ChatShareButton;
